import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function OrderSessionDialog({
  open,
  onClose,
  formData,
  onFormDataChange,
  onCreate,
  creating,
  error,
  apiResponse,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [pricingOptions, setPricingOptions] = React.useState([]);
  const [pricingLoading, setPricingLoading] = React.useState(false);
  const [pricingError, setPricingError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      const fetchPricing = async () => {
        setPricingLoading(true);
        setPricingError(null);
        try {
          const token = await getAccessTokenSilently().catch(() => null);
          const response = await fetch(`${API_BASE_URL}/admin/squarespace/prices`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch pricing: ${response.status}`);
          }

          const responseData = await response.json();
          const pricingData = Array.isArray(responseData.data) ? responseData.data : (responseData.pricing || []);
          const pricingList = Array.isArray(pricingData) ? pricingData : [];
          const websitePricing = pricingList.filter(item =>
            item?.type === 'website' ||
            item?.category === 'website' ||
            (item?.type !== 'domain' && !item?.tld && !item?.extension)
          );
          setPricingOptions(websitePricing.length > 0 ? websitePricing : pricingList);
        } catch (err) {
          console.error("Error fetching pricing:", err);
          setPricingError(err.message || "Failed to fetch pricing options");
        } finally {
          setPricingLoading(false);
        }
      };

      fetchPricing();
    }
  }, [open, getAccessTokenSilently]);

  const handleClose = () => {
    onFormDataChange({
      email: "",
      firstName: "",
      lastName: "",
      pricingId: "",
      title: "",
      domain: "",
    });
    onClose();
  };

  const isSuccess = apiResponse?.success === true || (apiResponse?.data != null && !apiResponse?.error);
  const showResult = !!apiResponse;

  const isFormValid = () => {
    return formData.email?.trim() &&
           formData.firstName?.trim() &&
           formData.lastName?.trim() &&
           formData.pricingId && formData.pricingId.trim() &&
           formData.title && formData.title.trim();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>Create Custom Order</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Use this when you are unable to create a customer account and need to use email and name instead.
          The account will be created automatically and will show in the order.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Order created successfully
          </Alert>
        )}
        {showResult && (
          <>
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
                fontFamily: "monospace",
                fontSize: "0.8rem",
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                API Response
              </Typography>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(apiResponse, null, 2)}
              </pre>
            </Box>
            {isSuccess && (() => {
              const orderData = apiResponse?.data;
              const orderId = orderData?.orderId ?? orderData?.id;
              const customerId = orderData?.customerId;
              const pricingId = orderData?.items?.[0]?.pricingId || formData.pricingId;
              if (!orderId && !customerId && !pricingId) return null;
              const metaObj = {
                ...(pricingId && { squarespace_pricing_id: pricingId }),
                ...(customerId && { squarespace_customer_id: customerId }),
                ...(orderId && { squarespace_order_id: orderId }),
              };
              if (Object.keys(metaObj).length === 0) return null;
              return (
                <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: "action.hover" }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Link order data in BryteBridge
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    To link this Squarespace order to BryteBridge, add these to meta_data:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                      fontSize: "0.875rem",
                      fontFamily: "monospace",
                      overflow: "auto",
                    }}
                  >
                    {JSON.stringify(metaObj, null, 2)}
                  </Box>
                </Box>
              );
            })()}
          </>
        )}
        {(!showResult || !isSuccess) && (
          <>
            {pricingError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {pricingError}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer (by Email)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => onFormDataChange({ ...formData, email: e.target.value })}
                  margin="dense"
                  size="small"
                  disabled={creating}
                  required
                  helperText="Customer email — finds existing or creates new customer"
                  placeholder="customer@example.com"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName || ""}
                  onChange={(e) => onFormDataChange({ ...formData, firstName: e.target.value })}
                  margin="dense"
                  size="small"
                  disabled={creating}
                  required
                  placeholder="First name"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName || ""}
                  onChange={(e) => onFormDataChange({ ...formData, lastName: e.target.value })}
                  margin="dense"
                  size="small"
                  disabled={creating}
                  required
                  placeholder="Last name"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Website Details
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth margin="dense" size="small" disabled={creating || pricingLoading}>
                  <InputLabel>Select Website Pricing</InputLabel>
                  <Select
                    value={formData.pricingId || ""}
                    onChange={(e) => onFormDataChange({ ...formData, pricingId: e.target.value })}
                    label="Select Website Pricing"
                  >
                    {pricingLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading pricing options...
                      </MenuItem>
                    ) : pricingOptions.length === 0 ? (
                      <MenuItem disabled>No pricing options available</MenuItem>
                    ) : (
                      pricingOptions.map((pricing) => {
                        const displayName = pricing.productName || pricing.name || pricing.tld || pricing.extension || pricing.id;
                        const price = pricing.price || pricing.registrationPrice || pricing.monthlyPrice || pricing.annualPrice;
                        const priceDisplay = price ? `$${(price > 10000 ? price / 100 : price).toFixed(2)}` : '';
                        const label = priceDisplay ? `${displayName} - ${priceDisplay}` : displayName;
                        return (
                          <MenuItem key={pricing.id || pricing.tld || pricing.extension} value={pricing.id}>
                            {label}
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Website Title"
                  value={formData.title || ""}
                  onChange={(e) => onFormDataChange({ ...formData, title: e.target.value })}
                  margin="dense"
                  size="small"
                  disabled={creating}
                  required
                  helperText="Title for the new website"
                  placeholder="e.g., My New Travel Website"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Domain (Optional)"
                  value={formData.domain || ""}
                  onChange={(e) => onFormDataChange({ ...formData, domain: e.target.value })}
                  margin="dense"
                  size="small"
                  disabled={creating}
                  helperText="Domain name (e.g., example.com)"
                  placeholder="e.g., example.com"
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          {isSuccess ? "Done" : "Cancel"}
        </Button>
        {!isSuccess && (
          <Button
            onClick={onCreate}
            variant="contained"
            disabled={!isFormValid() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? "Creating..." : "Create Order"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
